import React from 'react'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import { Dispatch, UnknownAction } from '@reduxjs/toolkit'
import { v4 as uuidv4 } from 'uuid'
import { Deferred } from 'utils'
import { isFunction } from 'lodash-es'
import { Translation } from 'locale'
import { Dialog, ShowDialogArgs, NotificationActions } from 'store/notification'
import { ModalOverlay } from './ModalOverlay'
import { ModalComponent, ModalContext } from './ModalContext'
import { DialogBox } from './DialogBox'
import { createDialogsSelector } from 'selectors'
import { ResponseMapResponse } from 'store/notification/notificationTypes'

interface ModalProviderProps {
  dialogs: Dialog[]
  dispatch: Dispatch<UnknownAction>
  children?: React.ReactNode
}

interface ModalEntry {
  id: string
  component: ModalComponent
  deferred: Deferred<{}> | Deferred<Translation>
}

interface ModalProviderState {
  contextEntries: ModalEntry[]
}

const mapStateToProps = createStructuredSelector({
  dialogs: createDialogsSelector()
})

export class ModalProviderComponent extends React.PureComponent<ModalProviderProps, ModalProviderState> {
  constructor(props: ModalProviderProps) {
    super(props)
    this.state = { contextEntries: [] }
  }

  render() {
    const { children, dialogs } = this.props
    const { contextEntries } = this.state

    const storeEntries = dialogs.map(dialog => ({
      id: dialog.id,
      component: <DialogBox {...dialog} onClose={(result: any) => this.handleCloseModal(dialog.id, result)} />
    }))

    /* Currently show store entries above the context entries */
    const modals = storeEntries.concat(...contextEntries)

    return (
      <ModalContext.Provider value={{ showModal: this.showModal, showDialog: this.showDialog }}>
        <ModalOverlay modals={modals} onCloseModal={this.handleCloseModal} />
        {children}
      </ModalContext.Provider>
    )
  }

  handleCloseModal = (id: string, result?: any) => {
    const { dispatch } = this.props
    const contextEntry = this.state.contextEntries.find(entry => entry.id === id)

    if (contextEntry) {
      this.setState({ contextEntries: this.state.contextEntries.filter(entry => entry.id !== id) })
      contextEntry.deferred.resolve(result)
    } else {
      const storeDialog = this.props.dialogs.find(dialog => dialog.id === id)

      this.props.dispatch(NotificationActions.dismissDialog(id))

      const responses = result && storeDialog && storeDialog.responseMap && storeDialog.responseMap[result as Translation]
      if (responses) {
        responses.forEach((response: ResponseMapResponse) => {
          isFunction(response) ? response() : dispatch(response)
        })
      }
    }
  }

  /* ModalContext */

  showModal = <T extends {}>(modal: ModalComponent): Promise<T> => {
    const deferred = new Deferred<T>()
    const id = uuidv4()
    const component = React.cloneElement(modal, { onClose: (result: any) => this.handleCloseModal(id, result) })
    this.setState(state => ({ contextEntries: [{ id, component, deferred }, ...state.contextEntries] }))
    return deferred.promise
  }

  showDialog = (options: ShowDialogArgs): Promise<Translation> => {
    return this.showModal(<DialogBox {...options} />)
  }
}

export const ModalProvider = connect(mapStateToProps)(ModalProviderComponent)
